import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Above BottomNav (z-50); cover full visual viewport including safe areas
      "fixed inset-0 z-[100] bg-slate-950/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

/**
 * Shell: flex column + max-height within visual viewport (soft keyboard).
 * Long forms: wrap fields in DialogBody; keep DialogFooter as sibling (not inside body)
 * so Mentés stays pinned (#16 / G8).
 */
export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, style, onOpenAutoFocus, onPointerDownOutside, onInteractOutside, ...props }, ref) => {
  const localRef = React.useRef<HTMLDivElement | null>(null);
  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref]
  );

  React.useEffect(() => {
    const el = localRef.current;
    const vv = window.visualViewport;
    if (!el || !vv) return;

    const sync = () => {
      // Soft keyboard: shrink max-height only. Do NOT rewrite `top` on scroll —
      // that made the dialog content appear to drag/shift while scrolling fields.
      if (vv.scale !== 1) return;
      const maxH = Math.max(240, Math.floor(vv.height * 0.88));
      el.style.maxHeight = `${maxH}px`;
    };

    sync();
    vv.addEventListener("resize", sync);
    return () => {
      vv.removeEventListener("resize", sync);
    };
  }, []);

  const isNestedOverlayTarget = (target: EventTarget | null) => {
    const t = target as HTMLElement | null;
    return Boolean(
      t?.closest?.(
        "[data-radix-select-content],[data-radix-popper-content-wrapper],[data-alert-dialog-overlay],[role='alertdialog'],[data-testid='confirm-dialog']"
      )
    );
  };

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setRefs}
        onOpenAutoFocus={(e) => {
          // Default: no autofocus (avoids iOS keyboard on open). Callers may override.
          if (onOpenAutoFocus) onOpenAutoFocus(e);
          else e.preventDefault();
        }}
        className={cn(
          // inset-0 + m-auto + h-fit centers without transform (transform breaks Select popper stacking/coords)
          "fixed inset-0 z-[100] m-auto flex h-fit w-[calc(100%-2rem)] max-w-md max-h-[min(88dvh,100dvh)] flex-col gap-3 overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-lg outline-none touch-pan-y dark:border-slate-800 dark:bg-slate-950",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-200 ease-out",
          className
        )}
        style={style}
        onPointerDownOutside={(e) => {
          // Keep select / confirm portals interactive while dialog is open
          if (isNestedOverlayTarget(e.target)) e.preventDefault();
          onPointerDownOutside?.(e);
        }}
        onInteractOutside={(e) => {
          if (isNestedOverlayTarget(e.target)) e.preventDefault();
          onInteractOutside?.(e);
        }}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:bg-slate-900 dark:hover:text-slate-50"
          aria-label="Bezárás"
        >
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = "DialogContent";

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex shrink-0 flex-col space-y-1 pr-8 text-center sm:text-left", className)}
      {...props}
    />
  );
}

/** Scrollable middle — use between DialogHeader and DialogFooter in long forms. */
export function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-testid="dialog-body"
      className={cn(
        "min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]",
        className
      )}
      {...props}
    />
  );
}

/** Pinned within DialogContent flex column (C5 / #16) — stays visible with soft keyboard. */
export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-testid="dialog-footer"
      className={cn(
        "flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-white pt-3 dark:border-slate-800 dark:bg-slate-950 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  );
}

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn("text-base font-semibold tracking-tight", className)} {...props} />
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-slate-600 dark:text-slate-400", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

/** Form shell: flex child of DialogContent so DialogBody scrolls and footer stays pinned. */
export const dialogFormClassName = "flex min-h-0 flex-1 flex-col gap-3";
